"use client";

import { useId } from "react";
import { FeedVideo } from "@/components/feed/feed-video";
import { AppImage } from "@/components/ui/app-image";
import { isVideoUrl, normalizeMediaArray } from "@/lib/media";
import { cn } from "@/lib/utils";

type ProfileMediaProps = {
  /**
   * A `MediaAsset`, an array of them, or the raw JSON column. The first asset
   * after order-sorting wins, so callers never have to normalise profile media
   * just to show it.
   */
  value?: unknown;
  /** Fallback URL when `value` has no usable asset (e.g. a legacy image). */
  src?: string | null;
  /** Poster for a `src`-only video (search results carry the two separately). */
  posterUrl?: string | null;
  alt: string;
  /**
   * Videos are letterboxed inside their frame instead of cropped. Images stay
   * `object-cover`; profile photos are already square at upload.
   */
  videoFit?: "cover" | "contain";
  /** Rendered when neither `value` nor `src` resolves. */
  fallback?: React.ReactNode;
  priority?: boolean;
  sizes?: string;
  className?: string;
};

/**
 * Profile media can be a photo OR a video (HLS). `<img src="…m3u8">` renders
 * nothing, which is why a profile whose first upload was a video showed a
 * broken tile on the profile page, Discover and Search.
 *
 * Videos here are GIFs, not players: muted, looping, autoplaying when visible,
 * with no sound button. They use the shared feed/autoplay registry so a page
 * with several of them still respects the decoder budget and pauses
 * off-screen. Images keep the AppImage path.
 */
export function ProfileMedia({
  value,
  src,
  posterUrl,
  alt,
  videoFit = "cover",
  fallback = null,
  priority = false,
  sizes,
  className,
}: ProfileMediaProps) {
  const audioId = `profile-media-${useId()}`;
  const media = normalizeMediaArray(
    Array.isArray(value) ? value : value ? [value] : [],
  ).at(0);
  const resolvedSrc = media?.url ?? src ?? null;
  const isVideo = media
    ? media.kind === "video"
    : Boolean(resolvedSrc && isVideoUrl(resolvedSrc));
  const resolvedPoster = media?.poster?.url ?? posterUrl ?? null;

  if (!resolvedSrc) {
    return <>{fallback}</>;
  }

  if (!isVideo) {
    return (
      <AppImage
        src={resolvedSrc}
        alt={alt}
        fill
        priority={priority}
        sizes={sizes}
        className={cn("object-cover", className)}
      />
    );
  }

  const contain = videoFit === "contain";

  return (
    <>
      {/* Letterboxing needs a backdrop: the poster, blurred, fills the bars. */}
      {contain && resolvedPoster ? (
        <AppImage
          src={resolvedPoster}
          alt=""
          fill
          disableBlur
          sizes={sizes}
          fallback={null}
          aria-hidden
          className="scale-110 object-cover opacity-60 blur-2xl"
        />
      ) : null}
      <FeedVideo
        audioId={audioId}
        src={resolvedSrc}
        posterUrl={resolvedPoster}
        blurHash={media?.blurHash}
        posterSizes={sizes}
        posterClassName={contain ? "object-contain" : "object-cover"}
        forceMuted
        ariaLabel={alt}
        className="h-full w-full"
        videoClassName={cn(
          contain ? "object-contain" : "object-cover",
          className,
        )}
      />
    </>
  );
}
